/**
 * Financial summary computation: aggregates financial data for a contact within a period.
 * 
 * All arithmetic in integer pence. Period filtering: FY vs all-time.
 * Reconciliation: totalOutstandingPence = totalInvoicedPence - totalPaidPence (exact).
 * 
 * Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */

import type { FinancialRecord } from '@akfdev-com/shared';
import type { FinancialSummary, SummaryPeriod } from './types';

/**
 * Determine if a date falls within the current UK financial year (06 Apr – 05 Apr).
 * 
 * The FY starts on 06 Apr and ends on 05 Apr of the following calendar year.
 * For example, FY 2024/25 runs 06 Apr 2024 – 05 Apr 2025.
 * 
 * @param date - The date to check
 * @param now - The current date (for determining the FY)
 * @returns True if the date falls within the active FY
 */
function isInActiveFY(date: Date, now: Date): boolean {
  // Determine the current FY based on the 'now' date
  // If 'now' is between 06 Apr and 31 Dec, FY starts in the same year
  // If 'now' is between 01 Jan and 05 Apr, FY started in the previous year
  
  const nowMonth = now.getMonth(); // 0-11
  const nowYear = now.getFullYear();

  let fyStartYear: number;
  if (nowMonth >= 3) { // April is month 3 (0-indexed)
    fyStartYear = nowYear;
  } else {
    fyStartYear = nowYear - 1;
  }

  const fyStart = new Date(fyStartYear, 3, 6); // 06 Apr
  const fyEnd = new Date(fyStartYear + 1, 3, 5); // 05 Apr of next year (inclusive end)

  return date >= fyStart && date <= fyEnd;
}

/**
 * Compute the financial summary for a contact within a period.
 * 
 * - period === 'FY': keeps records in the active UK financial year (06 Apr – 05 Apr)
 * - period === 'all-time': keeps all records
 * - All arithmetic in integer pence
 * - totalOutstandingPence = totalInvoicedPence - totalPaidPence (exact)
 * - lastInvoiceDate / lastPaymentDate = most-recent per category (or null)
 * - periodLabel = exactly 'FY' or 'all-time'
 * - Empty period yields zero totals and null last dates
 * 
 * @param records - Financial records (already owner + contact scoped by caller)
 * @param period - The period to aggregate over
 * @param now - Current date (for FY determination and invoice/payment dating)
 * @returns FinancialSummary with aggregated figures
 */
export function computeSummary(
  records: FinancialRecord[],
  period: SummaryPeriod,
  now: Date,
): FinancialSummary {
  // Filter records by period
  const filteredRecords = records.filter((record) => {
    if (period === 'all-time') {
      return true;
    }
    // period === 'FY'
    const recordDate = new Date(record.date);
    return isInActiveFY(recordDate, now);
  });

  // Initialize accumulators
  let totalInvoicedPence = 0;
  let totalPaidPence = 0;
  let lastInvoiceDate: string | null = null;
  let lastPaymentDate: string | null = null;

  // Process each record
  for (const record of filteredRecords) {
    // Accumulate totals
    totalInvoicedPence += record.amount || 0;
    totalPaidPence += record.amountPaid || 0;

    // Track last invoice date
    if (record.date && (!lastInvoiceDate || record.date > lastInvoiceDate)) {
      lastInvoiceDate = record.date;
    }

    // Track last payment date (only if amountPaid > 0)
    if (record.amountPaid && record.amountPaid > 0) {
      if (!lastPaymentDate || record.date > lastPaymentDate) {
        lastPaymentDate = record.date;
      }
    }
  }

  // Compute outstanding (exact integer pence, no rounding)
  const totalOutstandingPence = totalInvoicedPence - totalPaidPence;

  // For now, totalOwed and totalOwing are aliases (future refinement may differentiate)
  const totalOwedPence = totalOutstandingPence;
  const totalOwingPence = totalOutstandingPence;

  return {
    totalInvoicedPence,
    totalPaidPence,
    totalOutstandingPence,
    totalOwedPence,
    totalOwingPence,
    lastInvoiceDate,
    lastPaymentDate,
    periodLabel: period,
  };
}
